/**
 * Centralised validators for the ProfileComplete v2.0 wizard.
 * All return '' on valid, an error message string on invalid.
 *
 * Indian-specific regexes:
 *   - Mobile:  /^[6-9]\d{9}$/      (10 digits, starts 6-9)
 *   - Pincode: /^\d{6}$/
 *   - PAN:     /^[A-Z]{5}\d{4}[A-Z]$/
 *   - Aadhaar: /^\d{12}$/
 *   - IFSC:    /^[A-Z]{4}0[A-Z0-9]{6}$/
 *   - GSTIN:   /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z\d]Z[A-Z\d]$/
 *
 * Validators do NOT trim by default — values are echoed back as the
 * user typed them. Trim happens at SAVE time in the shell handlers if
 * the backend wants normalized data.
 */

const NAME_REGEX     = /^[a-zA-Z\s.'-]+$/;        // letters + spaces + . ' - (allows "M. K. Sharma", "O'Brien")
const MOBILE_REGEX   = /^[6-9]\d{9}$/;
const EMAIL_REGEX    = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PINCODE_REGEX  = /^\d{6}$/;
const PAN_REGEX      = /^[A-Z]{5}\d{4}[A-Z]$/;
const AADHAAR_REGEX  = /^\d{12}$/;
const IFSC_REGEX     = /^[A-Z]{4}0[A-Z0-9]{6}$/;
// Standard 15-char GSTIN: 2 state digits · 10 PAN · 1 entity (1-9 or A-Z) · 'Z' · 1 checksum
const GSTIN_REGEX    = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[A-Z\d]$/;
const ACCOUNT_REGEX  = /^\d{9,18}$/;

/* ── Personal name fields ─ no trim, raw string in/out ───────────────── */
export const validateFirstName = (v) => {
    const s = v ?? '';
    if (!s) return 'First name is required.';
    if (s.length < 2) return 'First name must be at least 2 characters.';
    if (s.length > 50) return 'First name must not exceed 50 characters.';
    if (!NAME_REGEX.test(s)) return 'First name can only contain letters and spaces.';
    return '';
};

export const validateLastName = (v) => {
    const s = v ?? '';
    if (!s) return 'Last name is required.';
    if (s.length > 50) return 'Last name must not exceed 50 characters.';
    if (!NAME_REGEX.test(s)) return 'Last name can only contain letters and spaces.';
    return '';
};

/* Generic person-name validator (partner / director / authorized person). */
export const validatePersonName = (v, { required = false, label = 'Name' } = {}) => {
    const s = v ?? '';
    if (!s) return required ? `${label} is required.` : '';
    if (s.length > 100) return `${label} must not exceed 100 characters.`;
    if (!NAME_REGEX.test(s)) return `${label} can only contain letters and spaces.`;
    return '';
};

export const validateMobile = (v) => {
    const s = v ?? '';
    if (!s) return 'Mobile number is required.';
    if (!/^\d{10}$/.test(s)) return 'Mobile number must be exactly 10 digits.';
    if (!MOBILE_REGEX.test(s)) return 'Enter a valid Indian mobile number (starts with 6, 7, 8, or 9).';
    return '';
};

export const validateEmail = (v) => {
    const s = v ?? '';
    if (!s) return 'Email is required.';
    if (!EMAIL_REGEX.test(s)) return 'Enter a valid email address.';
    if (s.length > 100) return 'Email must not exceed 100 characters.';
    return '';
};

export const validatePincode = (v, { required = false } = {}) => {
    const s = v ?? '';
    if (!s) return required ? 'Pincode is required.' : '';
    if (!PINCODE_REGEX.test(s)) return 'Pincode must be exactly 6 digits.';
    return '';
};

export const validatePAN = (v, { required = false } = {}) => {
    const s = (v ?? '').toUpperCase();
    if (!s) return required ? 'PAN is required.' : '';
    if (!PAN_REGEX.test(s)) return 'PAN must follow ABCDE1234F format.';
    return '';
};

export const validateAadhaar = (v, { required = false } = {}) => {
    const s = (v ?? '').replace(/\s+/g, '');
    if (!s) return required ? 'Aadhaar is required.' : '';
    if (!AADHAAR_REGEX.test(s)) return 'Aadhaar must be exactly 12 digits.';
    return '';
};

export const validateIFSC = (v, { required = false } = {}) => {
    const s = (v ?? '').toUpperCase();
    if (!s) return required ? 'IFSC code is required.' : '';
    if (!IFSC_REGEX.test(s)) return 'IFSC must be 4 letters + 0 + 6 alphanumeric (e.g. HDFC0001234).';
    return '';
};

export const validateGSTIN = (v, { required = false } = {}) => {
    const s = (v ?? '').toUpperCase();
    if (!s) return required ? 'GSTIN is required.' : '';
    if (!GSTIN_REGEX.test(s)) return 'GSTIN must be 15 characters in the standard format (e.g. 22AAAAA0000A1Z5).';
    return '';
};

export const validateAccountNumber = (v, { required = false } = {}) => {
    const s = v ?? '';
    if (!s) return required ? 'Account number is required.' : '';
    if (!ACCOUNT_REGEX.test(s)) return 'Account number must be 9 to 18 digits.';
    return '';
};

export const validateRequired = (v, label = 'This field') => {
    const s = typeof v === 'string' ? v : v;
    if (s == null || s === '' || (Array.isArray(s) && s.length === 0)) {
        return `${label} is required.`;
    }
    return '';
};

export const validateMaxLength = (v, max, label = 'This field') => {
    const s = v ?? '';
    if (s.length > max) return `${label} must not exceed ${max} characters.`;
    return '';
};

/* ════════════════════════════════════════════════════════════════════
 * FILE UPLOAD SAFETY
 * ════════════════════════════════════════════════════════════════════ */

/* Hard blacklist of executable / script extensions. Files matching these
   are rejected even if the backend's `allowed_formats` somehow permits
   them. Defence-in-depth against an attacker uploading a renamed binary. */
const DANGEROUS_EXTENSIONS = new Set([
    'exe', 'bat', 'cmd', 'com', 'scr', 'msi', 'jar',
    'sh', 'ps1', 'vbs', 'js', 'mjs', 'cjs', 'py', 'pl',
    'dll', 'so', 'dylib',
    'php', 'asp', 'aspx', 'jsp', 'rb',
    'app', 'apk', 'ipa',
    'lnk', 'iso', 'reg',
]);

/* Whitelist of MIME types we accept across the wizard. Mirror this with
   the backend's accepted MIME types. Anything else gets rejected even
   if the extension looks valid (catches renamed binaries). */
const ALLOWED_MIME_TYPES = new Set([
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
]);

/* Map of extension → expected MIME types. If the file's extension is in
   `allowed_formats` but the MIME doesn't match, we reject. */
const EXTENSION_MIME_MAP = {
    pdf: ['application/pdf'],
    jpg: ['image/jpeg', 'image/jpg'],
    jpeg: ['image/jpeg', 'image/jpg'],
    png: ['image/png'],
};

/**
 * File-type validation:
 *   1. Reject if extension is in the dangerous-extension blacklist
 *   2. Reject if extension isn't in the per-document `allowedFormats` whitelist
 *   3. Reject if the file's MIME type isn't in our global allowed list
 *   4. Reject if the MIME doesn't match what the extension claims
 *
 * Returns '' on valid, error message on invalid.
 */
export const validateFileType = (file, allowedFormats) => {
    if (!file) return 'No file selected.';

    const name = (file.name || '').toLowerCase();
    const ext = name.split('.').pop();
    const mime = (file.type || '').toLowerCase();

    // 1. Dangerous extension blacklist (always reject, no override possible)
    if (DANGEROUS_EXTENSIONS.has(ext)) {
        return `${ext.toUpperCase()} files are not allowed for security reasons.`;
    }

    // 2. Per-doc allowed-formats whitelist
    if (Array.isArray(allowedFormats) && allowedFormats.length > 0) {
        const allowedLower = allowedFormats.map((f) => f.toLowerCase());
        if (!allowedLower.includes(ext)) {
            const list = allowedFormats.map((f) => f.toUpperCase()).join(', ');
            return `Only ${list} files are allowed.`;
        }
    }

    // 3. Global MIME whitelist
    if (mime && !ALLOWED_MIME_TYPES.has(mime)) {
        return 'Unsupported file type. Only PDF, JPG, JPEG, and PNG are accepted.';
    }

    // 4. Extension/MIME consistency check (catches renamed binaries)
    if (mime && EXTENSION_MIME_MAP[ext] && !EXTENSION_MIME_MAP[ext].includes(mime)) {
        return `File extension says .${ext.toUpperCase()} but the content doesn't match. Choose a real ${ext.toUpperCase()} file.`;
    }

    return '';
};

export const validateFileSize = (file, maxBytes) => {
    if (!file || !maxBytes) return '';
    if (file.size > maxBytes) {
        const mb = (maxBytes / (1024 * 1024)).toFixed(1);
        return `File is too large. Maximum size is ${mb} MB.`;
    }
    return '';
};

/* ════════════════════════════════════════════════════════════════════
 * BACKEND ERROR MAPPING (Laravel 422 standard shape)
 * ════════════════════════════════════════════════════════════════════ */

/**
 * Extract field errors from a Laravel-style 422 response.
 *
 * Standard Laravel shape:
 *   {
 *     "message": "The given data was invalid.",
 *     "errors": {
 *       "mobile_number": ["The mobile number has already been taken."],
 *       "email":         ["Email is required."]
 *     }
 *   }
 *
 * Returns an object: { fieldName: 'first error message' } so each step's
 * inline error renderer can pick up the message and display it under the
 * field. Additional non-field errors (and message) are returned in a
 * separate `general` array for banner/toast display.
 */
export const parseBackendErrors = (error) => {
    const out = { fields: {}, general: [] };
    if (!error) return out;

    // Network error / no response
    if (!error.response) {
        out.general.push(error.message || 'Network error. Please check your connection.');
        return out;
    }

    const data = error.response.data || {};
    const status = error.response.status;

    // Laravel 422 validation errors
    if (status === 422 && data.errors && typeof data.errors === 'object') {
        Object.entries(data.errors).forEach(([field, messages]) => {
            if (Array.isArray(messages) && messages.length > 0) {
                out.fields[field] = messages[0];
            } else if (typeof messages === 'string') {
                out.fields[field] = messages;
            }
        });
    }

    // Generic message (rate limit, auth error, server error, business rule)
    if (data.message && (status !== 422 || Object.keys(out.fields).length === 0)) {
        out.general.push(data.message);
    } else if (status === 401) {
        out.general.push('Your session has expired. Please log in again.');
    } else if (status === 403) {
        out.general.push('You do not have permission to perform this action.');
    } else if (status === 429) {
        out.general.push('Too many requests. Please wait a moment and try again.');
    } else if (status >= 500) {
        out.general.push('The server is having trouble. Please try again in a moment.');
    }

    return out;
};
